import dayjs from 'dayjs';
import { TransactionType } from '@mx51/spi-client-js';
import { ITxFlow } from '../redux/reducers/TerminalSlice/interfaces';
import { getTxTypeByPosRefId } from '../utils/tx-utils';
import { calculateCashoutOnlyTotalAmount, calculateTotalAmount } from '../utils/common/helpers';
import { CloudTransactionResponse, CloudTransactionResultAmounts } from '../transaction-handling/cloud-transaction';
import { SpiCloudPairing } from '../redux/reducers/PairingSlice/interfaces';

export const TX_LOG_KEY = 'TransactionLog';

// Cloud only requires enough information to display on the transaction log
export interface CloudTxLog {
  loggedAtTsMs: number; // Sub-optimal, but the API doesn't provide us an actual timestamp
  pairingId: string;
  pairingNickname: string; // Pairing nickname or last 6 chars of pairing ID
  source: 'Integrated';
  status: 'Success' | 'Failed' | 'Unknown';
  tid: string;
  totalCents?: number;
  transactionType: string;
}

// Local requires the kitchen sink because it's used for display across a lot of pages
export interface LocalTxLog {
  completedTime: number;
  mid: string;
  posId: string;
  posRefId: string;
  receipt: string;
  successState: string;
  tid: string;
  type: string;
  override?: boolean;
  amountCents: number;
  purchaseAmount: number;
  surchargeAmount: number;
  bankCashAmount: number;
  tipAmount: number;
  preAuthAmount?: number;
  topupAmount?: number;
  reduceAmount?: number;
  preAuthId?: string;
  hostResponseText: string;
  transactionType: string;
  total: number;
  source: 'Integrated' | 'Pay At Table';
}

export type CloudTxLogItem = {
  body: CloudTxLog;
  key: string;
  type: 'cloud';
};

export type LocalTxLogItem = {
  body: LocalTxLog;
  key: string;
  type: 'local';
};

export type TxLogItem = CloudTxLogItem | LocalTxLogItem;

export class TxLogService {
  static updateTx(tx: TxLogItem) {
    const existing = TxLogService.load();

    if (!existing.some((storedTx) => storedTx.key === tx.key)) {
      // eslint-disable-next-line no-console
      console.error(`TxLogService.updateTx() called with unknown key "${tx.key}"`);
      return existing;
    }

    return TxLogService.saveTxns(existing.map((storedTx) => (storedTx.key === tx.key ? tx : storedTx)));
  }

  static load() {
    const storedValue = localStorage.getItem(TX_LOG_KEY);

    if (!storedValue) {
      return [];
    }

    let txLog: TxLogItem[];

    try {
      txLog = JSON.parse(storedValue);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('TxLogService.load() failed to parse localStorage, resetting', error);
      return TxLogService.saveTxns([]);
    }

    // Filter out any transactions completed before today
    const todaysTxns = txLog.filter((tx: TxLogItem) => {
      if (!('type' in tx)) {
        // Filter out legacy values
        return false;
      }

      if (tx.type === 'cloud') {
        return dayjs(tx.body.loggedAtTsMs).isSame(dayjs(), 'day');
      }

      if (tx.type === 'local') {
        return dayjs(tx.body.completedTime).isSame(dayjs(), 'day');
      }

      return false;
    });

    if (todaysTxns.length !== txLog.length) {
      // Write back to storage if we filtered out any txns
      return TxLogService.saveTxns(todaysTxns);
    }

    return todaysTxns;
  }

  static saveTx(tx: TxLogItem) {
    const existing = TxLogService.load();

    if (existing.some((storedTx) => storedTx.key === tx.key)) {
      // eslint-disable-next-line no-console
      console.warn(`TxLogService.saveTx() called with existing key "${tx.key}", updating instead`);
      return TxLogService.updateTx(tx);
    }

    return TxLogService.saveTxns([...existing, tx]);
  }

  static removeTx(key: string) {
    return TxLogService.saveTxns(TxLogService.load().filter((tx) => tx.key !== key));
  }

  static saveTxns(txns: TxLogItem[]) {
    localStorage.setItem(TX_LOG_KEY, JSON.stringify(txns));
    return txns;
  }

  static findCloudTxByTransactionId(transactionId: string) {
    const storedTx = TxLogService.load().find((tx) => tx.key === transactionId);

    if (storedTx?.type === 'cloud') {
      return storedTx;
    }

    return undefined;
  }

  static findLocalTxByPosRefId(posRefId: string) {
    const storedTx = TxLogService.load().find((tx) => tx.key === posRefId);

    if (storedTx?.type === 'local') {
      return storedTx;
    }

    return undefined;
  }
}

function deriveCloudTransactionType(resultAmounts?: CloudTransactionResultAmounts) {
  if (!resultAmounts) {
    return 'Purchase';
  }

  const { moto_amount, refund_amount, cashout_amount, purchase_amount } = resultAmounts;

  if (moto_amount > 0) {
    return 'MOTO';
  }

  if (refund_amount > 0) {
    return 'Refund';
  }

  if (cashout_amount > 0 && purchase_amount === 0) {
    return 'Cashout';
  }

  return 'Purchase';
}

function mapCloudFinancialStatus(financialStatus?: string) {
  if (financialStatus === 'APPROVED') {
    return 'Success';
  }

  if (financialStatus === 'DECLINED') {
    return 'Failed';
  }

  return 'Unknown';
}

function calculateCloudTotalCents(resultAmounts?: CloudTransactionResultAmounts) {
  if (!resultAmounts) {
    return undefined;
  }

  const { purchase_amount, cashout_amount, moto_amount, refund_amount, surcharge_amount, tip_amount } = resultAmounts;

  return purchase_amount + cashout_amount + moto_amount + refund_amount + surcharge_amount + tip_amount;
}

export const TxLogServiceMapper = {
  fromCloudTransaction({
    transactionResponse,
    pairing,
  }: {
    transactionResponse: CloudTransactionResponse;
    pairing: SpiCloudPairing;
  }): CloudTxLogItem {
    const { data } = transactionResponse;

    if (!data?.id) {
      throw new Error('Cannot create TxLogItem without a transaction id');
    }

    const pairingNickname = pairing.pairingNickname || pairing.pairingId.slice(-6);
    const resultAmounts = data.result_amounts;
    const status = mapCloudFinancialStatus(data.result_financial_status);
    const totalCents = calculateCloudTotalCents(resultAmounts);
    const transactionType = deriveCloudTransactionType(resultAmounts);

    return {
      body: {
        loggedAtTsMs: Date.now(),
        pairingId: pairing.pairingId,
        pairingNickname,
        source: 'Integrated',
        status,
        tid: pairing.tid,
        totalCents,
        transactionType,
      },
      key: data.id,
      type: 'cloud',
    };
  },

  fromLocalTransaction({
    txFlow,
    posId,
    tid,
    mid,
  }: {
    txFlow: ITxFlow;
    posId: string;
    tid: string;
    mid: string;
  }): LocalTxLogItem {
    const {
      request,
      response,
      success: successState,
      completedTime,
      type,
      posRefId,
      receipt: transactionReceipt,
      override,
    } = txFlow;
    const { preAuthId, hostResponseText, transactionType } = response.data;

    const { purchaseAmount, surchargeAmount, bankCashAmount, tipAmount, preAuthAmount, topupAmount, reduceAmount } =
      override ? request.data : response.data;

    const amountCents = txFlow.amountCents || purchaseAmount;

    // TODO: put this logic into a normalized calculate total
    // instead of splitting it out.
    const total =
      type === TransactionType.CashoutOnly
        ? calculateCashoutOnlyTotalAmount({
            amountCents,
            surchargeAmount,
            bankCashAmount,
            tipAmount,
          })
        : calculateTotalAmount({
            amountCents,
            surchargeAmount,
            bankCashAmount,
            tipAmount,
          });

    return {
      body: {
        successState,
        completedTime,
        type: getTxTypeByPosRefId(posRefId),
        posRefId,
        posId,
        tid,
        mid,
        receipt: transactionReceipt,
        override,
        amountCents,
        purchaseAmount,
        surchargeAmount,
        bankCashAmount,
        tipAmount,
        preAuthAmount,
        topupAmount,
        reduceAmount,
        preAuthId,
        hostResponseText,
        transactionType,
        total,
        source: 'Integrated',
      },
      key: posRefId,
      type: 'local',
    };
  },
};
